import { type ReactElement, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import classNames from 'classnames'
import type { PopperProps } from '@mui/material/Popper'
import {
  DropdownPlaceholder,
  EnumDropdownItemVariant,
  type IDropdownItem,
  type IDropdownPlaceholderProps,
  type IDropdownProps,
} from 'shared/ui/Dropdown'
import { DayjsFormats, logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import {
  type ITextFieldProps,
  type IInputNumericProps,
  type ITextFieldPlaceholderProps,
  makeTextFieldAutoFocus,
  makeTextFieldIcon,
  TextField,
} from 'shared/ui/TextField'
import { MINUTES_IN_DAY } from 'shared/ui/DatePicker/lib/constants'
import styles from '../styles.module.scss'

type ITimePickerProps = {
  selected?: Date | null
  onChange: (date: Date | null) => void
  filteredPassedTime?: Date
  filteredRemainingTime?: Date
  offsetMinutes?: number
  minuteStep?: number
  parentsDropdownId?: string[]
  disabled?: boolean
  textFieldPlaceholderProps?: Partial<ITextFieldPlaceholderProps>
  defaultValue?: Date
  onChangeOpen?: IDropdownProps['onChangeOpen']
  triggerTooltipProps?: IDropdownPlaceholderProps['triggerTooltipProps']
  PopperProps?: Partial<PopperProps>
  topListTextFieldProps?: Partial<ITextFieldProps<IInputNumericProps>>
  isTopReverse?: boolean
  customContent?: ({
    onBlur,
  }: {
    onBlur: () => void
    onChangeRaw: (value: string) => void
    inputValue: string
    dropdownItems: IDropdownItem[]
    handleChange: (item: IDropdownItem) => void
  }) => ReactElement
}

export const TimePicker = ({
  selected,
  onChange,
  filteredPassedTime,
  filteredRemainingTime,
  offsetMinutes = 0,
  minuteStep = 15,
  parentsDropdownId,
  disabled,
  textFieldPlaceholderProps,
  defaultValue,
  onChangeOpen,
  topListTextFieldProps,
  isTopReverse,
  customContent,
  ...props
}: ITimePickerProps) => {
  const refSearch = useRef('')
  const [search, setSearch] = useState('')
  const refFocus = useRef(false)
  const [isOpen, setIsOpen] = useState(false)
  const handleChange = (item: IDropdownItem) => {
    const value = new Date(String(item.value))
    refFocus.current = false

    onChange(value)
  }

  const onBlur = () => {
    const promise = new Promise<void>((resolve, reject) => {
      setTimeout(() => {
        if (!refFocus.current) {
          setSearch('')
          refSearch.current = ''
          return
        }
        refFocus.current = false
        let date: Date | null = null
        const search = refSearch.current
        try {
          if (!search.trim()) {
            throw 'Date invalid'
          }
          const split = search.split(' ')
          let hours = 0
          let minutes = 0
          const left = split[0]
          const right = split[1]

          if (left) {
            const leftSplit = left.split(':')
            const hoursPart = leftSplit[0]
            const minutesPart = leftSplit[1]
            if (hoursPart) {
              const parseHours = parseInt(hoursPart)
              if (parseHours <= 12) {
                hours = parseHours
              } else {
                throw 'Date invalid'
              }
            }
            if (minutesPart) {
              const parseMinutes = parseInt(minutesPart)
              if (parseMinutes <= 60) {
                minutes = parseMinutes
              } else {
                throw 'Date invalid'
              }
            }
          }
          const isAm = right?.toLowerCase() === 'am'
          let isPm = right?.toLowerCase() === 'pm'

          if (right && !isAm && !isPm) {
            throw 'Date invalid'
          }
          if (!right && !isPm) {
            isPm = dayjs().get('hours') > 12
          }
          const pmHours = isPm && hours < 12 ? 12 : 0

          let additionHours = 0
          if (isAm) {
            additionHours = hours < 12 ? hours : 0
          } else {
            additionHours = hours + pmHours
          }

          date = dayjs(selected)
            .startOf('day')
            .add(additionHours, 'hour')
            .add(minutes, 'minutes')
            .toDate()
          onChange(date)
        } catch (e) {
          reject(e)

          onChange(defaultValue || (selected && dayjs(selected).toDate()) || null)
        } finally {
          setSearch('')
          refSearch.current = ''
        }
      }, 100)
    })

    promise.catch((error) => logger.error(error))
  }

  const onChangeRaw = (value: string) => {
    refFocus.current = true
    setSearch(value)
    refSearch.current = value
  }

  const time = selected ? dayjs(selected).format(DayjsFormats.time2) : ''

  const items: IDropdownItem[] = useMemo(() => {
    const selectedDate = selected || new Date()
    const itemsCount = MINUTES_IN_DAY / minuteStep

    return Array(itemsCount)
      .fill(0)
      .map((_, index) => {
        const dayjsDate = dayjs(selectedDate)
          .startOf('day')
          .add(index * minuteStep * 60, 'seconds')
        const label = dayjsDate.format(DayjsFormats.time2)

        const isPassedTime = () => {
          const currentDate = uiStore
            .dayjs(filteredPassedTime)
            .add(offsetMinutes, 'minutes')
            .toDate()
          const selectedDate = dayjsDate.toDate()

          return currentDate.getTime() > selectedDate.getTime()
        }

        const isRemainingTime = () => {
          const currentDate = uiStore
            .dayjs(filteredRemainingTime)
            .add(offsetMinutes, 'minutes')
            .toDate()
          const selectedDate = dayjsDate.toDate()

          return currentDate.getTime() < selectedDate.getTime()
        }

        return {
          id: dayjsDate.toString(),
          value: dayjsDate.toString(),
          disabled:
            (filteredPassedTime && isPassedTime()) || (filteredRemainingTime && isRemainingTime()),
          label,
          variant: EnumDropdownItemVariant.CheckedRight,
          activeValue: label === time,
        }
      })
  }, [selected, time, filteredPassedTime, filteredRemainingTime, minuteStep])

  const filterItems = search
    ? items.filter((item) => item.label.toLowerCase().startsWith(search.trim().toLowerCase()))
    : items

  const dropdownItems = filterItems.length ? filterItems : items

  const inputValue = refFocus.current ? search : time

  if (customContent) {
    return customContent({ onBlur, onChangeRaw, inputValue, dropdownItems, handleChange })
  }

  return (
    <DropdownPlaceholder
      items={dropdownItems}
      onChange={handleChange}
      parentsDropdownId={parentsDropdownId}
      propsDropdownList={{
        className: styles.timePickerDropdown,
        isTopReverse,
      }}
      topListComponent={(onCloseMenu) => (
        <div
          className={classNames(styles.wrapTimePicker, {
            [styles.wrapTimePicker__reverse]: isTopReverse,
          })}
        >
          <TextField
            variant={'integrated'}
            size={'small'}
            InputProps={{
              placeholder: topListTextFieldProps?.InputProps?.placeholder || 'Select time',
              onKeyDown: (event) => {
                if (event.code === 'Enter') {
                  onBlur()
                  onCloseMenu()
                }
              },
            }}
            disabled={disabled}
            value={inputValue}
            onChange={onChangeRaw}
            onBlur={onBlur}
            mainActions={[makeTextFieldAutoFocus({ focusTrigger: isOpen ? 'open' : '' })]}
            leftActions={[
              makeTextFieldIcon({ icon: 'clock', className: styles.timePickerActions }),
            ]}
            {...topListTextFieldProps}
          />
        </div>
      )}
      placeholder={time || textFieldPlaceholderProps?.placeholder}
      textPlaceholderProps={{
        variant: 'stroke',
        size: 'small',
        leftActions: [makeTextFieldIcon({ icon: 'clock', className: styles.timePickerActions })],
        ...textFieldPlaceholderProps,
        className: classNames(
          styles.timePicker,
          { [styles.timePicker__placeholder]: !selected },
          textFieldPlaceholderProps?.className
        ),
      }}
      additionalWidth={8}
      offset={{ y: -4 }}
      onChangeOpen={(value) => {
        setIsOpen(value)
        onChangeOpen?.(value)
      }}
      {...props}
    />
  )
}
